import React, { createContext, useContext, useState, useEffect, useMemo } from "react";

const dict = {
  en: {
    appName: "S2S — SOUNDS TO SPACE",
    tagline: "Your Legacy Among The Stars",
    hero_title: "Send Your Voice Beyond Earth",
    hero_sub: "Transform your memories, messages and stories into encrypted digital transmissions prepared for deep-space communication.",
    start: "Start Transmission",
    explore: "Explore Missions",
    nav: {
      dashboard: "Mission Control",
      upload: "Upload Center",
      galaxy: "Galactic Map",
      vault: "Space Vault",
      family: "Family Vault",
      legacy: "Legacy Mode",
      globe: "Global Map",
      pricing: "Plans",
      admin: "Admin",
      login: "Sign In",
      register: "Enlist",
      logout: "Sign Out",
    },
    auth: {
      email: "Email",
      password: "Password",
      name: "Full Name",
      signin: "Authenticate",
      signup: "Create Mission Account",
      have_account: "Already enlisted?",
      need_account: "New operator?",
    },
    dashboard: {
      title: "Mission Control",
      active: "Active Missions",
      pending: "Pending Transmissions",
      completed: "Completed Transmissions",
      storage: "Storage Used",
      history: "Transmission History",
      capsules: "Capsules Curated",
      no_history: "No transmissions yet. Begin a mission to populate your log.",
    },
    upload: {
      title: "Upload Center",
      subtitle: "Drop files to package them for deep-space transmission",
      dropzone: "Drop files or click to select",
      hint: "Audio · Video · Images · PDFs · Documents · Text — up to 5 GB",
      package: "Package Size",
      next: "Continue to Capsule Builder",
      uploading: "Transmitting to vault…",
      files_selected: "files selected",
      file_count: "Files",
    },
    capsule: {
      title: "AI Capsule Builder",
      subtitle: "Claude Sonnet 4.5 is curating your capsule",
      name: "Capsule Name",
      summary: "Capsule Summary",
      file_count: "File Count",
      size: "Package Size",
      date: "Creation Date",
      hint: "Briefly describe the intention of this capsule (optional)",
      regenerate: "Re-curate with AI",
      transmit: "Initiate Transmission",
      generating: "AI is curating your capsule…",
    },
    enc: {
      title: "Space Encryption Sequence",
      step1: "Encrypting Data — AES-256",
      step2: "Generating Digital Signature — SHA-512",
      step3: "Creating Deep-Space Package",
      step4: "Preparing Transmission",
      telemetry: "TELEMETRY",
    },
    cmd: {
      title: "Transmission Command Center",
      mission: "Mission",
      transmission: "Transmission ID",
      hash: "Digital Hash",
      size: "Package Size",
      timeline: "Mission Timeline",
      cert: "Download Your Intergalactic Certificate",
      advance: "Advance Stage",
      done: "Signal Successfully Launched",
      status_MISSION_STARTED: "Mission Started",
      status_SIGNAL_LEAVING_EARTH: "Signal Leaving Earth",
      status_PASSING_LUNAR_ORBIT: "Passing Lunar Orbit",
      status_LEAVING_SOLAR_SYSTEM: "Leaving Solar System",
      status_CROSSING_HELIOSPHERE: "Crossing Heliosphere",
      status_ENTERING_INTERSTELLAR_SPACE: "Entering Interstellar Space",
      status_APPROACHING_DESTINATION: "Approaching Destination",
      status_MISSION_COMPLETE: "Mission Complete",
    },
    galaxy: {
      title: "Galactic Navigation",
      open: "Open Galactic Map",
    },
    vault: {
      title: "Space Vault",
      subtitle: "Encrypted personal archive of your transmissions and files",
      tab_files: "Files",
      tab_capsules: "Capsules",
      tab_tx: "Transmissions",
      empty: "Vault is empty. Begin your first transmission.",
    },
    family: {
      title: "Family Vault",
      subtitle: "Shared capsules and legacy projects with your closest constellation",
      invite: "Invite Family Member",
      coming: "Family Vault opens with Pioneer & Legacy plans.",
    },
    legacy: {
      title: "Legacy Mode",
      subtitle: "Preserve family histories, voices, photos and personal messages along an immersive timeline",
      coming: "Activate Legacy by upgrading to the Legacy plan.",
    },
    globe: {
      title: "Global Missions Map",
      total: "Total Transmissions",
      active: "Active Missions",
      countries: "Countries",
    },
    pricing: {
      title: "Choose Your Mission",
      subtitle: "From a single deep-space message to a multigenerational legacy vault.",
      cta: "Select Plan",
      enterprise_cta: "Contact Mission Ops",
      popular: "Most Selected",
    },
    payment: {
      title: "Payment",
      method: "Method",
      pix: "PIX (BRL)",
      usdt: "USDT (TRC20)",
      btc: "Bitcoin (BTC)",
      wallet: "Send to",
      tx_ref: "Transaction Reference",
      submit: "Confirm Payment",
      submitted: "Payment under verification. We will notify you once confirmed.",
      amount: "Amount",
    },
    common: {
      back: "Back",
      cancel: "Cancel",
      save: "Save",
      yes: "Yes",
      no: "No",
    },
    pillars: {
      deepspace_title: "Deep-Space Ready",
      deepspace_desc: "AES-256 encryption + SHA-512 signatures, prepared for licensed space communication infrastructure.",
      vault_title: "Encrypted Vault",
      vault_desc: "Your memories live inside a private, signed vault — yours and yours alone.",
      map_title: "Anonymous Mission Map",
      map_desc: "See the world joining the silent broadcast — without ever revealing identities.",
    },
    footer: {
      copyright: "Prepared for licensed space communication infrastructure.",
      plans: "Plans",
      globe: "Globe",
      enlist: "Enlist",
    },
  },
  pt: {
    appName: "S2S — SONS PARA O ESPAÇO",
    tagline: "Sua Mensagem Além da Terra",
    hero_title: "Envie Sua Voz Para Além da Terra",
    hero_sub: "Transforme suas memórias, mensagens e histórias em transmissões digitais criptografadas, preparadas para comunicação no espaço profundo.",
    start: "Iniciar Transmissão",
    explore: "Explorar Missões",
    nav: {
      dashboard: "Controle de Missão",
      upload: "Central de Upload",
      galaxy: "Mapa Galáctico",
      vault: "Cofre Espacial",
      family: "Cofre Familiar",
      legacy: "Modo Legado",
      globe: "Mapa Global",
      pricing: "Planos",
      admin: "Admin",
      login: "Entrar",
      register: "Alistar-se",
      logout: "Sair",
    },
    auth: {
      email: "Email",
      password: "Senha",
      name: "Nome Completo",
      signin: "Autenticar",
      signup: "Criar Conta de Missão",
      have_account: "Já alistado?",
      need_account: "Novo operador?",
    },
    dashboard: {
      title: "Controle de Missão",
      active: "Missões Ativas",
      pending: "Transmissões Pendentes",
      completed: "Transmissões Concluídas",
      storage: "Armazenamento Usado",
      history: "Histórico de Transmissões",
      capsules: "Cápsulas Curadas",
      no_history: "Nenhuma transmissão ainda. Inicie uma missão para popular o registro.",
    },
    upload: {
      title: "Central de Upload",
      subtitle: "Solte arquivos para empacotá-los para transmissão",
      dropzone: "Solte arquivos ou clique para selecionar",
      hint: "Áudio · Vídeo · Imagens · PDFs · Documentos · Texto — até 5 GB",
      package: "Tamanho do Pacote",
      next: "Continuar para a Cápsula",
      uploading: "Transmitindo para o cofre…",
      files_selected: "arquivos selecionados",
      file_count: "Arquivos",
    },
    capsule: {
      title: "Construtor de Cápsula com IA",
      subtitle: "Claude Sonnet 4.5 está curando sua cápsula",
      name: "Nome da Cápsula",
      summary: "Resumo da Cápsula",
      file_count: "Quantidade de Arquivos",
      size: "Tamanho do Pacote",
      date: "Data de Criação",
      hint: "Descreva brevemente a intenção desta cápsula (opcional)",
      regenerate: "Recurar com IA",
      transmit: "Iniciar Transmissão",
      generating: "A IA está curando sua cápsula…",
    },
    enc: {
      title: "Sequência de Criptografia Espacial",
      step1: "Criptografando Dados — AES-256",
      step2: "Gerando Assinatura Digital — SHA-512",
      step3: "Criando Pacote de Espaço Profundo",
      step4: "Preparando Transmissão",
      telemetry: "TELEMETRIA",
    },
    cmd: {
      title: "Central de Comando de Transmissão",
      mission: "Missão",
      transmission: "ID de Transmissão",
      hash: "Hash Digital",
      size: "Tamanho do Pacote",
      timeline: "Linha do Tempo da Missão",
      cert: "Baixar sua Certidão Intergaláctica",
      advance: "Avançar Estágio",
      done: "Sinal Lançado com Sucesso",
      status_MISSION_STARTED: "Missão Iniciada",
      status_SIGNAL_LEAVING_EARTH: "Sinal Deixando a Terra",
      status_PASSING_LUNAR_ORBIT: "Passando Órbita Lunar",
      status_LEAVING_SOLAR_SYSTEM: "Saindo do Sistema Solar",
      status_CROSSING_HELIOSPHERE: "Cruzando Heliopausa",
      status_ENTERING_INTERSTELLAR_SPACE: "Entrando no Espaço Interestelar",
      status_APPROACHING_DESTINATION: "Aproximando-se do Destino",
      status_MISSION_COMPLETE: "Missão Concluída",
    },
    galaxy: {
      title: "Navegação Galáctica",
      open: "Abrir Mapa Galáctico",
    },
    vault: {
      title: "Cofre Espacial",
      subtitle: "Arquivo pessoal criptografado de suas transmissões e arquivos",
      tab_files: "Arquivos",
      tab_capsules: "Cápsulas",
      tab_tx: "Transmissões",
      empty: "O cofre está vazio. Comece sua primeira transmissão.",
    },
    family: {
      title: "Cofre Familiar",
      subtitle: "Cápsulas compartilhadas e projetos de legado com sua constelação mais próxima",
      invite: "Convidar Membro da Família",
      coming: "O Cofre Familiar abre com os planos Pioneer & Legacy.",
    },
    legacy: {
      title: "Modo Legado",
      subtitle: "Preserve histórias familiares, vozes, fotos e mensagens em uma linha do tempo imersiva",
      coming: "Ative o Legacy fazendo o upgrade para o plano Legacy.",
    },
    globe: {
      title: "Mapa Global de Missões",
      total: "Total de Transmissões",
      active: "Missões Ativas",
      countries: "Países",
    },
    pricing: {
      title: "Escolha Sua Missão",
      subtitle: "De uma única mensagem de espaço profundo a um cofre de legado multigeracional.",
      cta: "Selecionar Plano",
      enterprise_cta: "Falar com Operações",
      popular: "Mais Escolhido",
    },
    payment: {
      title: "Pagamento",
      method: "Método",
      pix: "PIX (BRL)",
      usdt: "USDT (TRC20)",
      btc: "Bitcoin (BTC)",
      wallet: "Enviar para",
      tx_ref: "Referência da Transação",
      submit: "Confirmar Pagamento",
      submitted: "Pagamento em verificação. Avisaremos quando confirmado.",
      amount: "Valor",
    },
    common: {
      back: "Voltar",
      cancel: "Cancelar",
      save: "Salvar",
      yes: "Sim",
      no: "Não",
    },
    pillars: {
      deepspace_title: "Pronto para o Espaço Profundo",
      deepspace_desc: "Criptografia AES-256 + assinaturas SHA-512, preparado para infraestrutura licenciada de comunicação espacial.",
      vault_title: "Cofre Criptografado",
      vault_desc: "Suas memórias vivem dentro de um cofre privado e assinado — exclusivamente seu.",
      map_title: "Mapa Anônimo de Missões",
      map_desc: "Veja o mundo aderindo à transmissão silenciosa — sem nunca revelar identidades.",
    },
    footer: {
      copyright: "Preparado para infraestrutura licenciada de comunicação espacial.",
      plans: "Planos",
      globe: "Globo",
      enlist: "Alistar-se",
    },
  },
};

const LangCtx = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("s2s_lang") || "en");
  const initialMount = useMemo(() => ({ done: false }), []);

  useEffect(() => {
    localStorage.setItem("s2s_lang", lang);
    // Skip the initial hydration mount — only sync to backend on
    // user-initiated language toggles to avoid server chatter.
    if (!initialMount.done) {
      initialMount.done = true;
      return;
    }
    const token = localStorage.getItem("s2s_token");
    if (!token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`;
    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ language: lang }),
    }).catch(() => {});
  }, [lang, initialMount]);

  const t = useMemo(() => {
    const tree = dict[lang] || dict.en;
    return (key) => {
      const parts = key.split(".");
      let node = tree;
      for (const p of parts) {
        if (node && typeof node === "object" && p in node) node = node[p];
        else return key;
      }
      return typeof node === "string" ? node : key;
    };
  }, [lang]);

  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);
